/**
 * DEPRECATED (Step 4.3): prefer backend GET /api/admin/units/{id}/co-living-metrics.
 *
 * Shared Co-Living / room-list metrics for admin unit list + unit detail.
 * Tenancy-driven occupancy and revenue; full potential from room list prices.
 *
 * Still consumed by AdminUnitDetailPage, AdminPropertyManagerDetailPage,
 * AdminApartmentsPage. Remove after those pages migrate to the backend endpoint.
 */

import {
  activeRoomsForUnit,
  canonicalUnitRoomCounts,
  getRoomOccupancyStatus,
  hasOccupancySnapshotError,
  isLandlordContractLeaseStarted,
  sumActiveTenancyMonthlyRentForUnit,
} from "./unitOccupancyStatus";

export function getRoomsForUnit(unitId, allRooms) {
  if (!allRooms || unitId == null || unitId === "") return [];
  const uid = String(unitId);
  return allRooms.filter(
    (room) => String(room.unitId || room.unit_id) === uid
  );
}

/**
 * The CURRENT occupancy counters for one unit (OC2b).
 *
 * This is the only part of `getCoLivingMetrics` OC2b owns. Everything else the
 * function returns -- `fullRevenue`, `currentRevenue`, `vacancyLoss`,
 * `leaseStarted` -- keeps its existing semantics and its existing input, which
 * is every room ROW of the unit. That split is deliberate and tested: a unit
 * with six rows of which two are retired still has a full-occupancy revenue
 * potential of all six rooms, while only four of them are capacity today.
 * Changing the money figure to match the occupancy figure would be a revenue
 * decision, and revenue is out of this slice's scope.
 *
 * ORDER OF PREFERENCE, the same one `getUnitOccupancyStatus` applies:
 *   1. the canonical Layer-1 counters on the unit row
 *   2. otherwise the unit's ACTIVE room rows, classified here
 *   3. an explicit enrichment failure -> four `null`s
 *
 * `null` IS AN ESTABLISHED VALUE here, not a new one: `unitListModel` already
 * documents these four slots as nullable for a business apartment, "rather than
 * reporting a 0/0 that reads like an empty co-living unit". A failed
 * calculation is the same statement.
 *
 * @returns {{occupied: number|null, reserved: number|null, free: number|null,
 *            total: number|null}}
 */
function currentOccupancyCounts(unit, allRooms, tenancies) {
  if (hasOccupancySnapshotError(unit)) {
    return { occupied: null, reserved: null, free: null, total: null };
  }

  const canonical = canonicalUnitRoomCounts(unit);
  if (canonical) {
    const free = Math.max(
      0,
      canonical.total - canonical.occupied - canonical.reserved
    );
    return {
      occupied: canonical.occupied,
      reserved: canonical.reserved,
      free,
      total: canonical.total,
    };
  }

  const active = activeRoomsForUnit(unit, allRooms);
  if (tenancies == null) {
    // "Not loaded" is not "everything free", but this function has never had a
    // way to say so and OC2b is not the slice that gives it one. What it does
    // fix is the population: a retired room no longer arrives as free capacity
    // while the page is still loading.
    return {
      occupied: 0,
      reserved: 0,
      free: active.length,
      total: active.length,
    };
  }

  let occupied = 0;
  let reserved = 0;
  for (const room of active) {
    const st = getRoomOccupancyStatus(room, tenancies);
    if (st === "belegt") occupied += 1;
    else if (st === "reserviert") reserved += 1;
  }
  return {
    occupied,
    reserved,
    free: active.length - occupied - reserved,
    total: active.length,
  };
}

export function getCoLivingMetrics(unit, allRooms, tenancies) {
  const leaseStarted = isLandlordContractLeaseStarted(unit);
  // EVERY ROW, FROZEN. The money and capacity figures are computed from this
  // set and OC2b does not narrow it -- see `currentOccupancyCounts`.
  const rooms = getRoomsForUnit(unit.unitId ?? unit.id, allRooms);
  const occupancy = currentOccupancyCounts(unit, allRooms, tenancies);

  if (rooms.length === 0) {
    // OC2b: this branch used to answer from `unit.rooms` and the legacy
    // `unit.occupiedRooms` form field -- a unit declaring nine rooms with no
    // room rows reported nine free rooms that do not exist. It now answers from
    // the canonical counters when the row carries them and from the (empty)
    // active-row set otherwise. `fullRevenue` and the money slots below stay
    // `null` exactly as before.
    return {
      occupiedCount: occupancy.occupied,
      reservedCount: occupancy.reserved,
      freeCount: occupancy.free,
      totalRooms: occupancy.total,
      fullRevenue: null,
      currentRevenue: null,
      vacancyLoss: null,
      currentProfit: null,
      runningCosts: null,
      leaseStarted,
    };
  }

  // THE FULL-OCCUPANCY POTENTIAL, over every room row of the unit. Unchanged by
  // OC2b: a retired room is not capacity today, but it is still a room whose
  // price belongs in "what this unit could earn". Frozen by an explicit test.
  const fullRevenue = rooms.reduce(
    (sum, room) => sum + Number(room.priceMonthly || 0),
    0
  );

  const activeRentFromTenancies =
    tenancies == null
      ? 0
      : sumActiveTenancyMonthlyRentForUnit(unit, tenancies);
  /** Sum of monthly_revenue_equivalent on tenancy rows (not backend KPI month). Unit detail overwrites with GET /admin/profit. */
  const currentRevenue = activeRentFromTenancies;
  const vacancyLoss =
    fullRevenue != null
      ? Math.max(0, Number(fullRevenue) - activeRentFromTenancies)
      : 0;

  return {
    occupiedCount: occupancy.occupied,
    reservedCount: occupancy.reserved,
    freeCount: occupancy.free,
    totalRooms: occupancy.total,
    fullRevenue,
    currentRevenue,
    vacancyLoss,
    currentProfit: null,
    runningCosts: null,
    leaseStarted,
  };
}
